import React from 'react'
import {observer} from 'mobx-react-lite'
import {Button, Flex, Stack, useDisclosure, Text, Box} from '../../../../vanilla'
import {Modal} from '../../../../components/modal'
import {ArrowRightIcon, ExclamationCircleIcon} from '../../../../components/icons'
import {useBasketStore} from '../../../../store/hooks/useStore'
import {DateCalendar} from '../../../../components/date-calendar'
import {TimeSlot} from '../../../../types/store/basket'
import {modalFooter, modalFooterButton} from './styles.css'

interface ChangingSlotWarningModalProps extends ReturnType<typeof useDisclosure> {
  onSubmit: () => void
  newSlot: TimeSlot | null
}

export const ChangingSlotWarningModal = observer(function ChangingSlotWarningModal(
  props: ChangingSlotWarningModalProps
) {
  const {onClose, onSubmit, newSlot} = props

  const {
    currentDeliverySlot: {timeSlot},
  } = useBasketStore()

  const currentSlotDate = timeSlot ? new Date(timeSlot.windowEndTime) : null
  const newSlotDate = newSlot ? new Date(newSlot.windowEndTime) : null

  return (
    <Modal size="full-lg" closeIcon={false} allowScroll={true} {...props}>
      <Stack width="full" spacing="0px">
        <Box paddingX="16px" paddingY={['16px', '16px', '24px']}>
          <Text variant="heading3" lineHeight="shorter" display="block" as="span" align="center">
            Change Delivery Slot?
          </Text>
        </Box>

        <Stack spacing={{desktop: '20px'}}>
          <Flex
            width="full"
            marginX="auto"
            bg="white"
            padding="20px"
            paddingTop={['4px', '0px', '20px']}
            gap="12px"
            justify="center"
            align="center"
          >
            {currentSlotDate && (
              <DateCalendar
                date={currentSlotDate}
                variant="accent"
                size="lg"
                timeRange={timeSlot ?? undefined}
              />
            )}
            <ArrowRightIcon boxSize="20px" color="textMuted" />
            {newSlotDate && (
              <DateCalendar
                date={newSlotDate}
                variant="accent"
                size="lg"
                timeRange={newSlot ?? undefined}
              />
            )}
          </Flex>
          <Flex
            align="stretch"
            border="1px"
            borderColor="promotionColorDark"
            borderRadius="base"
            marginBottom="24px"
            marginX={['16px', '24px']}
          >
            <Flex
              align="center"
              width="52px"
              bg="promotionColorDark"
              flexShrink="0"
              height="auto"
              justify="center"
            >
              <ExclamationCircleIcon color="white" boxSize="20px" />
            </Flex>
            <Box padding="12px">
              <Text variant="text3">
                If you change your delivery slot you may notice some changes to your basket
              </Text>
            </Box>
          </Flex>
        </Stack>

        <Flex
          gap={['12px', '12px', '20px']}
          align="center"
          justify={['flex-start', 'flex-start', 'center']}
          direction={['column-reverse', 'column-reverse', 'row']}
          paddingX={['12px', '28px']}
          paddingTop="12px"
          paddingBottom="28px"
          className={modalFooter}
        >
          <Button
            width="full"
            size="sm"
            variant="outlineDark"
            onClick={() => onClose()}
            extraClassName={modalFooterButton}
          >
            Cancel Change
          </Button>
          <Button
            width="full"
            size="sm"
            data-test-selector="confirm-change-slot"
            variant="primary"
            onClick={() => {
              onSubmit()
              onClose()
            }}
            extraClassName={modalFooterButton}
          >
            Confirm
          </Button>
        </Flex>
      </Stack>
    </Modal>
  )
})
